import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { Box } from "@mui/material";

import ReactECharts from "echarts-for-react";
import IconTooltip from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import { AutoCompleteDropdown } from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";

import { useDualYAxisChart } from "hooks/useDualYAxisChart";
import { Loading } from "features/common";
import CustomLegend from "./CustomLegend";

const DualYAxisChartContainer = ({
  data,
  yAxisInfos,
  selectedYAxis,
  setSelectedYAxis,
  colors,
  chartInfos,
  isLoading,
  totalStats,
  customStyle,
  otherCustomSetting = {
    formatXAxisLabel: {
      open: false,
      format: "DD MMM",
    },
  },
}) => {
  const [chartOptions, setChartOptions] = useState(null);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const hasCustomLegend = totalStats && Object.keys(totalStats).length > 0;

  const { formatMessage } = useIntl();
  const { getChartOptions } = useDualYAxisChart({
    data,
    yAxisInfos,
    selectedYAxis,
    colors,
  });

  const dropdownOptions = yAxisInfos.map((axisInfo) => ({
    label: axisInfo.name,
    value: axisInfo.key,
  }));

  useEffect(() => {
    if (chartInfos.length > 0) {
      setChartOptions(
        chartInfos.map((chartInfo) =>
          getChartOptions(chartInfo.type, otherCustomSetting, hasCustomLegend)
        )
      );
    }
  }, [chartInfos]);

  return (
    <Box width="100%" sx={{ ...customStyle?.containerStyle }}>
      {chartInfos.map((chartInfo, index) => (
        <Box sx={{ paddingBottom: "24px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
              {chartInfo.title && (
                <Box fontSize={16} color="#555">
                  {formatMessage({
                    id: chartInfo.title,
                  })}
                </Box>
              )}
              {chartInfo.tooltip && (
                <IconTooltip
                  content={formatMessage({
                    id: chartInfo.tooltip,
                  })}
                  customColor="#A5A5A5"
                />
              )}
            </Box>
            {chartInfo.showYAxisDropdown && (
              <Box
                sx={{
                  minWidth: "230px",
                  ...customStyle?.dropdownStyle,
                }}
              >
                <AutoCompleteDropdown
                  size="small"
                  value={dropdownOptions
                    .filter((option) => selectedYAxis?.includes(option.value))
                    .sort(
                      (a, b) =>
                        selectedYAxis.indexOf(a.value) -
                        selectedYAxis.indexOf(b.value)
                    )}
                  multiple
                  // Commenting out max prop to support show alert message when user select more than 2 options
                  // max={2}
                  options={yAxisInfos.map((axisInfo) => ({
                    label: axisInfo.label,
                    value: axisInfo.key,
                  }))}
                  onChange={(event, newValue) => {
                    if (setSelectedYAxis) {
                      if (newValue.length > 2) {
                        setShowAlertMessage(true);
                      } else {
                        setShowAlertMessage(false);
                        setSelectedYAxis(newValue.map((item) => item.value));
                      }
                    }
                  }}
                  placeholder={formatMessage({
                    id: "global.dualYAxis.selectDataType.placeholder",
                  })}
                  customNodeInOption={
                    <Box paddingLeft="16px" mb="8px">
                      <Box
                        color={(theme) => theme.customColors.grey[600]}
                        sx={{ fontSize: "12px", fontWeight: 600 }}
                      >
                        {showAlertMessage ? (
                          <Box color={(theme) => theme.customColors.pink[600]}>
                            {formatMessage({
                              id: "global.dualYAxis.selectDataType.reachOptionLimit",
                            })}
                          </Box>
                        ) : (
                          <Box>
                            {formatMessage(
                              {
                                id: "global.dualYAxis.selectDataType.optionLimit",
                              },
                              { value: ` ${selectedYAxis.length}/2` }
                            )}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </Box>
            )}
          </Box>
          <Box
            height={customStyle?.echartStyle?.height || "310px"}
            width={"99%"}
            position={"relative"}
          >
            {isLoading ? (
              <Loading open={isLoading} fullScreen={false} isChart={true} />
            ) : !chartInfo.hasData ? (
              <Box
                position="absolute"
                width="100%"
                className="item-flex"
                justifyContent="center"
                height="100%"
                flexDirection="column"
              >
                <img src={require("assets/img/barChartAlt.png")} />
                <Box color="#555" fontWeight={700} m="12px 0 8px">
                  {formatMessage({
                    id: "global.chart.emptyStatus.title",
                  })}
                </Box>
                <Box textAlign="center" color="#777" width="188px">
                  {formatMessage({
                    id: "global.chart.emptyStatus.body",
                  })}
                </Box>
              </Box>
            ) : (
              <>
                {hasCustomLegend && (
                  <CustomLegend
                    totalStats={totalStats}
                    colors={colors}
                    selectedYAxis={selectedYAxis}
                  />
                )}
                {chartOptions && (
                  <ReactECharts
                    option={chartOptions[index]}
                    autoResize={true}
                    style={{ height: "100%", ...customStyle?.echartStyle }}
                    notMerge={true}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default DualYAxisChartContainer;
